import type { TaskDetail } from '@fos/shared/contracts/work';
import { addDaysLocal, todayLocal } from '@fos/shared/time';
import { ChevronDown, Clock } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { formatDuration } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Section } from '../../../ui/Section';
import { TimeEntryRows, type TimeEntryRowsFilter } from '../../time';

/**
 * Czas na karcie zadania (Figma 04: stopka „Czas · 2 h 05 min”): suma i wpisy zadania wszystkich osób w zakresie
 * (ostatni rok), rozwijane jak Historia. „Dodaj czas” w nagłówku karty otwiera formularz modułu Czas z zadaniem.
 * Bez modułu Czas nic się nie pokazuje.
 */

/** Wpisy zadania z ostatniego roku (limit zakresu `time.list`: 366 dni). */
export function taskTimeFilter(taskId: string, today = todayLocal()): TimeEntryRowsFilter {
  return { taskId, scope: 'team', from: addDaysLocal(today, -365), to: today };
}

export function TaskTimeToggle({
  task,
  open,
  onToggle,
}: {
  task: Pick<TaskDetail, 'id'>;
  open: boolean;
  onToggle: () => void;
}) {
  const access = useAccess();
  const canTime = access.can('time');
  const query = trpc.time.list.useQuery(taskTimeFilter(task.id), { enabled: canTime });
  if (!canTime) return null;
  const seconds = query.data?.totals.seconds ?? 0;
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls="czas-zadania"
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 rounded-sm hover:text-fg focus-visible:outline-2 focus-visible:outline-accent max-md:min-h-11"
    >
      <Clock size={14} strokeWidth={1.75} aria-hidden />
      {seconds > 0 ? `Czas · ${formatDuration(seconds)}` : 'Czas'}
      <ChevronDown
        size={14}
        strokeWidth={1.75}
        aria-hidden
        className={cn('transition-transform', open && 'rotate-180')}
      />
    </button>
  );
}

export function TaskTimeSection({ task }: { task: Pick<TaskDetail, 'id'> }) {
  return (
    <Section id="czas-zadania" aria-label="Czas zadania" className="mt-4">
      <TimeEntryRows
        filter={taskTimeFilter(task.id)}
        showWeeks={false}
        emptyTitle="Brak wpisów czasu"
        emptyDescription="Czas dodasz przyciskiem „Dodaj czas” na górze karty."
      />
    </Section>
  );
}
